import {
  isCashierJobTitle,
  isCashierManagerJobTitle,
} from "./orgHelpers";

function isTerminated(emp) {
  return emp?.status === "terminated" || emp?.status === "TERMINATED";
}

function reportingParentIds(emp) {
  const ids = [];
  if (emp?.managerId) ids.push(String(emp.managerId));
  if (
    emp?.directManagerId &&
    String(emp.directManagerId) !== String(emp.managerId)
  ) {
    ids.push(String(emp.directManagerId));
  }
  return ids;
}

export function uniqueEmployeesById(employees = []) {
  const seen = new Set();
  return employees.filter((emp) => {
    if (!emp?.id) return false;
    const key = String(emp.id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Everyone who reports to `managerId`, directly or through nested managers,
 * down to the lowest rank. Does not include the manager themselves.
 */
export function collectSubordinateEmployees(
  employees = [],
  managerId,
  { includeTerminated = false } = {},
) {
  if (!managerId) return [];

  const rootId = String(managerId);
  const childrenByManager = new Map();

  employees.forEach((emp) => {
    if (!emp?.id) return;
    if (!includeTerminated && isTerminated(emp)) return;
    reportingParentIds(emp).forEach((parentId) => {
      if (!childrenByManager.has(parentId)) {
        childrenByManager.set(parentId, []);
      }
      childrenByManager.get(parentId).push(emp);
    });
  });

  const result = [];
  const visited = new Set([rootId]);
  const queue = [rootId];

  while (queue.length > 0) {
    const currentId = queue.shift();
    const children = childrenByManager.get(currentId) || [];
    children.forEach((child) => {
      const childId = String(child.id);
      if (visited.has(childId)) return;
      visited.add(childId);
      result.push(child);
      queue.push(childId);
    });
  }

  return result;
}

function findEmployeeById(employees, employeeId) {
  if (!employeeId) return null;
  return (
    (employees || []).find((emp) => String(emp?.id) === String(employeeId)) ||
    null
  );
}

/** Field team leader must not keep appeals from the branch they left. */
export function isCrossBranchAppealForUser(appeal, user, employees) {
  if (!user?.branchId || !appeal) return false;
  const roles = user.backendRoles || [];
  if (
    roles.includes("branch_manager") ||
    roles.includes("regional_manager") ||
    roles.includes("department_manager")
  ) {
    return false;
  }
  const isFieldLeader =
    user.role === "manager" ||
    roles.includes("manager") ||
    roles.includes("team_leader") ||
    roles.includes("supervisor");
  if (!isFieldLeader) return false;
  const employee = findEmployeeById(employees, appeal.employeeId);
  const employeeBranch = employee?.branchBackendId || employee?.branchId;
  if (!employeeBranch) return false;
  return String(employeeBranch) !== String(user.branchId);
}

function isHigherThanFieldLeader(emp) {
  const slugs = emp?.roleSlugs || [];
  if (
    slugs.includes("branch_manager") ||
    slugs.includes("regional_manager") ||
    slugs.includes("department_manager")
  ) {
    return true;
  }
  return (
    emp?.memberLevel === "branch-manager" ||
    emp?.memberLevel === "regional-manager" ||
    emp?.memberLevel === "department-manager" ||
    emp?.jobTitle === "مدير فرع"
  );
}

function isStaleSameBranchManager(manager, employeeBranch) {
  if (!manager) return true;
  if (isTerminated(manager)) return true;
  if (isHigherThanFieldLeader(manager)) return true;
  const managerBranch = manager.branchBackendId || manager.branchId;
  if (!managerBranch) return false;
  return String(managerBranch) !== String(employeeBranch);
}

/** Appeal still points at a manager who now works in another branch. */
export function isOrphanedSameBranchAppeal(appeal, user, employees) {
  if (!user?.branchId || !appeal) return false;
  const employee = findEmployeeById(employees, appeal.employeeId);
  const employeeBranch = employee?.branchBackendId || employee?.branchId;
  if (!employeeBranch || String(employeeBranch) !== String(user.branchId)) {
    return false;
  }
  if (!employee?.managerId && !appeal.managerId) return true;
  const manager = findEmployeeById(
    employees,
    employee?.managerId || appeal.managerId,
  );
  return isStaleSameBranchManager(manager, employeeBranch);
}

export function isEmployeeUnderManager(employees, managerId, employeeId) {
  if (!managerId || !employeeId) return false;
  if (String(managerId) === String(employeeId)) return false;
  return collectSubordinateEmployees(employees, managerId, {
    includeTerminated: true,
  }).some((emp) => String(emp.id) === String(employeeId));
}

function excludeCashiering(employees) {
  return employees.filter(
    (emp) =>
      !isCashierJobTitle(emp.jobTitle) &&
      !isCashierManagerJobTitle(emp.jobTitle),
  );
}

/**
 * Team list for فريقي / الجزاءات: reporting tree to the lowest rank,
 * plus the broader branch / region / department scope those roles already had.
 */
export function buildManagerTeamMembers({
  employeeRows = [],
  user,
  isGm = false,
  isBranchManager = false,
  isRegionalManager = false,
  isDeptManager = false,
} = {}) {
  if (isGm || !user?.employeeId) return [];

  const managerId = user.employeeId;
  const tree = collectSubordinateEmployees(employeeRows, managerId, {
    includeTerminated: true,
  }).filter((emp) => String(emp.id) !== String(managerId));

  const branchScoped = isBranchManager
    ? employeeRows.filter(
        (emp) =>
          String(emp.id) !== String(managerId) &&
          emp.branchBackendId === user.branchId,
      )
    : [];

  const managedBranchIds = user.managedBranchIds || [];
  const regionScoped = isRegionalManager
    ? employeeRows.filter(
        (emp) =>
          String(emp.id) !== String(managerId) &&
          emp.branchBackendId &&
          managedBranchIds.includes(emp.branchBackendId),
      )
    : [];

  const deptScoped = isDeptManager
    ? employeeRows.filter(
        (emp) =>
          String(emp.id) !== String(managerId) &&
          emp.departmentBackendId === user.departmentBackendId,
      )
    : [];

  const byId = new Map(
    employeeRows.filter((emp) => emp?.id).map((emp) => [String(emp.id), emp]),
  );
  const fieldLeaderBranchId =
    !isGm &&
    !isBranchManager &&
    !isRegionalManager &&
    !isDeptManager &&
    user?.branchId
      ? String(user.branchId)
      : null;
  const scopedTree = fieldLeaderBranchId
    ? tree.filter(
        (emp) => String(emp.branchBackendId || "") === fieldLeaderBranchId,
      )
    : tree;
  const orphanedSameBranch = fieldLeaderBranchId
    ? employeeRows.filter((emp) => {
        if (!emp?.id || String(emp.id) === String(managerId)) return false;
        if (String(emp.branchBackendId || "") !== fieldLeaderBranchId) {
          return false;
        }
        if (
          isCashierJobTitle(emp.jobTitle) ||
          isCashierManagerJobTitle(emp.jobTitle)
        ) {
          return false;
        }
        if (emp.memberLevel && emp.memberLevel !== "employee") return false;
        if (!emp.managerId) return true;
        const manager = byId.get(String(emp.managerId));
        return isStaleSameBranchManager(manager, fieldLeaderBranchId);
      })
    : [];

  const merged = uniqueEmployeesById([
    ...scopedTree,
    ...orphanedSameBranch,
    ...branchScoped,
    ...regionScoped,
    ...deptScoped,
  ]);

  if (isBranchManager || isRegionalManager) {
    return excludeCashiering(merged);
  }

  return merged;
}
